import { useState, useCallback } from "react";
import { RootState } from "redux/store";
import { Button } from "react-bootstrap";
import { Table } from "components";
import { getRowIds } from "helpers/data/dataHandling";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { Manufacturers } from "popups/Manufacturers/Manufacturers";
import { updatePreferredBrandField } from "redux/slices/PreferredBrands";
import { getPbsManufacturers } from "redux/slices/PbsManufacturers";
import { getPreferredBrandDetails } from "redux/slices/PreferredBrands";

export function Manufacturer() {
    const dispatch = useAppDispatch();
    const manufacturers = useAppSelector((state: RootState) => state.pbsManufacturers);
    const [showPopup, setShowPopup] = useState(false);
    const preferredBrands = useAppSelector((state: RootState) => state.preferredBrands);

    function customCells(row: any) {
        return (
            <>
                <div className="ps-1 pe-3" style={{ marginTop: "3px", marginBottom: "3px" }}>
                    {row.value}
                </div>
            </>
        );
    };

    const columns = [
        {
            Header: "Rank",
            accessor: "rank",
            width: 150,
            Cell: (row: any) => {
                const id: number = parseInt(row.cell.row.id);
                const idPlus: number = parseInt(row.cell.row.id) + 1;
                return (<div className="d-flex">
                    <div className="ps-1 pe-3">{idPlus}</div>
                    <div className="rank-actions">
                        {id < preferredBrands.pbsManufacturers.length - 1 ? (
                            <Button variant="light" className="btn-icon me-1 table-action-btn" onClick={() => moveManufacturerDown(id)}>
                                <i className="uil-arrow-down"></i>
                            </Button>
                        ) : null
                        }
                        {id > 0 ? (
                            <Button variant="light" className="btn-icon me-1 table-action-btn" onClick={() => moveManufacturerUp(id)}>
                                <i className="uil-arrow-up"></i>
                            </Button>
                        ) : null
                        }
                    </div>
                </div>
                );
            }
        },
        {
            Header: "Manufacturer",
            accessor: "name",
            width: 300,
            Cell: (row: any) => customCells(row),
        },
        {
            Header: "Code",
            accessor: "code",
            width: 850,
            Cell: (row: any) => customCells(row),
        },
    ];

    function moveManufacturerUp(id: number) {
        let manufacturerLevel = [...preferredBrands.pbsManufacturers];
        const manufacturer = manufacturerLevel[id];
        if (id > 0) {
            manufacturerLevel[id] = manufacturerLevel[id - 1];
            manufacturerLevel[id - 1] = manufacturer;
        }
        dispatch(updatePreferredBrandField({ key: "pbsManufacturers", value: manufacturerLevel }));
    }

    function moveManufacturerDown(id: number) {
        let manufacturerLevel = [...preferredBrands.pbsManufacturers];
        const manufacturer = manufacturerLevel[id];
        if (id < manufacturerLevel.length - 1) {
            manufacturerLevel[id] = manufacturerLevel[id + 1];
            manufacturerLevel[id + 1] = manufacturer;
        }
        dispatch(updatePreferredBrandField({ key: "pbsManufacturers", value: manufacturerLevel }));
    }

    async function manufacturersOnSave() {
        let selectedManufacturers: any[] = [];
        let deseledManufacturers: any[] = [];
        let preferredBrandsManufacturersList: any[] = [...preferredBrands.pbsManufacturers];
        const preferredBrandsManufacturersListIds: any[] = [...preferredBrands.pbsManufacturers.map((manufacturer: any) => manufacturer.pbsManufacturerId)];
        let manufacturersList: any[] = [];
        selectedManufacturers = getRowIds(manufacturers.selectedRows);
        deseledManufacturers = getRowIds(manufacturers.deselectedRows);

        await dispatch(getPbsManufacturers({})).then((res) => manufacturersList = res.data);

        if (manufacturers.selectAllFilter) {
            manufacturersList.forEach((manufacturer: any) => {
                if (!preferredBrandsManufacturersListIds.includes(manufacturer.id) && !deseledManufacturers.includes(manufacturer.id)) {
                    preferredBrandsManufacturersList.push({
                        pbsManufacturerId: manufacturer.id,
                        rank: 0,
                        code: manufacturer.code,
                        name: manufacturer.name,
                    });
                }
            });
        } else {
            selectedManufacturers.forEach((manufacturer: any) => {
                if (!preferredBrandsManufacturersListIds.includes(manufacturer)) {
                    const manufacturerFilterted = manufacturersList.filter((manufacturerItem: any) => manufacturerItem.id === manufacturer);
                    if (manufacturerFilterted.length > 0) {
                        preferredBrandsManufacturersList.push({
                            pbsManufacturerId: manufacturerFilterted[0].id,
                            rank: 0,
                            code: manufacturerFilterted[0].code,
                            name: manufacturerFilterted[0].name,
                        });
                    }
                }
            });
        }
        const preferredBrandsManufacturersIndexes = preferredBrandsManufacturersList.map((manufacturer: any, index: number) => ({ ...manufacturer, rank: index + 1 }));
        dispatch(updatePreferredBrandField({ key: "pbsManufacturers", value: preferredBrandsManufacturersIndexes }));
        setShowPopup(false);
    }

    const selectedRows = useCallback((pageIndex: any) => {
        dispatch(updatePreferredBrandField({ key: "selectedManufacturersRows", value: pageIndex }));
    }, []);

    function handleDelete() {
        let selectedManufacturersRows = preferredBrands.selectedManufacturersRows.map((row: any) => preferredBrands.pbsManufacturers[parseInt(row)].pbsManufacturerId);
        const manufacturers = preferredBrands.pbsManufacturers.filter((row: any) => !selectedManufacturersRows.includes(row.pbsManufacturerId));
        const manufacturersWithIndexes = manufacturers.map((manufacturer: any, index: number) => ({ ...manufacturer, rank: index + 1 }));
        dispatch(updatePreferredBrandField({ key: "manufacturers", value: manufacturersWithIndexes }));
    }

    return (
        <>
            <Manufacturers show={showPopup} onClose={() => setShowPopup(false)} onSave={manufacturersOnSave} />
            <Table
                maxHeight={450}
                pageSize={15}
                isSortable={true}
                pagination={true}
                isSelectable={true}
                isSearchable={true}
                columns={columns}
                loading={preferredBrands.loading}
                data={preferredBrands.pbsManufacturers}
                selectedRows={selectedRows}
                removeRows={handleDelete}
                addNewRow={() => setShowPopup(true)}
                reloadTableRows={() => dispatch(getPreferredBrandDetails({ ids: [preferredBrands.id], loadDetails: ['Manufacturers', 'Stock', 'Stores']  }))}
            />
        </>
    );
}